import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { Subject, timer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '@/auth/services';
import { AppLoaderComponent, DataTableComponent } from '@/components';
import { RateListingPreset } from '@/enums';
import { buildRateColumns } from '@/factories';
import {
    applyDisplayAgencyNameToFilterObject,
    applyDisplayAgencyNameToQuery,
    applyDistrictAndCompetitorToFilterObject,
    applyDistrictAndCompetitorToQuery,
} from '@/helpers';
import { extractSpreadsheetHeaderRow, missingPqgRatesRequiredColumns, PQG_RATES_OPTIONAL_COLUMN_LABELS, PQG_RATES_REQUIRED_COLUMN_LABELS } from '@/helpers/pqg-rates-upload-columns';
import { DataTableColumn, Rate, RatesAutocomplete, RatesOptions, RatesVisibilitySyncStatus } from '@/interfaces';
import { RatesService } from '@/service';
import { AppService } from '@/service/app.service';

@Component({
    selector: 'app-rate-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    styleUrls: ['./listing.component.scss'],
    imports: [DataTableComponent, ButtonModule, Dialog, FileUpload, AppLoaderComponent]
})
export class RateListingComponent implements OnInit, OnChanges {
    @Input() groupBy: string | null = null;
    @Input() preset: RateListingPreset = RateListingPreset.DEFAULT;
    @Input() title = 'Rates';
    @Input() displayAgencyName: string | null = null;

    reloadTable$ = new Subject<void>();
    columns: DataTableColumn<Rate>[] = [];
    options?: RatesOptions;
    districtMappingId: string | null | undefined;
    competitorId: string | null | undefined;
    pqgUploadRatedModalOpened = false;

    private readonly destroyRef = inject(DestroyRef);

    @ViewChild('pqgFileUpload') pqgFileUpload?: FileUpload;

    readonly pqgRatesRequiredColumns = PQG_RATES_REQUIRED_COLUMN_LABELS;
    readonly pqgRatesOptionalColumns = PQG_RATES_OPTIONAL_COLUMN_LABELS;

    pqgUploadMissingColumns: string[] | null = null;
    pqgUploadFileError: string | null = null;
    file: File | null = null;
    modalLoading = false;
    visibilitySyncStatus: RatesVisibilitySyncStatus | null = null;

    private lastReloadedSyncRunId: string | null = null;

    constructor(
        public authService: AuthService,
        private service: RatesService,
        private route: ActivatedRoute,
        private router: Router,
        private appService: AppService
    ) {
        this.service.options().subscribe((options) => {
            this.options = options;
        });
        this.districtMappingId = this.route?.snapshot.paramMap.get('district_mapping_id');
        this.competitorId = this.route?.snapshot.paramMap.get('competitor_id');
    }

    ngOnInit(): void {
        this.setColumns();
        this.startVisibilitySyncPolling();
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            this.applyQueryParamsToColumns(params);
            queueMicrotask(() => this.reloadTable$.next());
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setColumns();
        const inputChangedAfterInit = Object.values(changes).some((c) => !c.firstChange);
        if (inputChangedAfterInit) {
            queueMicrotask(() => this.reloadTable$.next());
        }
    }

    loadData = (params: Record<string, any>) => {
        params = this.applyContextFilters(params);
        if (this.groupBy) {
            params = { ...params, group_by: this.groupBy };
        }

        return this.service.listing(params);
    };

    exportData = (params: Record<string, any>) => {
        params = this.applyContextFilters(params, true);
        if (this.groupBy) {
            params = { ...params, group_by: this.groupBy };
        }

        return this.service.export(params);
    };

    ratesAutocomplete(key: keyof RatesAutocomplete, query: string, context: Record<string, any> = {}) {
        return this.service.autocomplete(key, query, this.applyContextFilters(context));
    }

    onPqgUploadModalHide(): void {
        this.pqgUploadRatedModalOpened = false;
        this.pqgUploadMissingColumns = null;
        this.pqgUploadFileError = null;
        this.file = null;
        this.pqgFileUpload?.clear();
        this.modalLoading = false;
    }

    onPqgFileChosen(): void {
        this.pqgUploadMissingColumns = null;
        this.pqgUploadFileError = null;
    }

    async onPqgRatesUpload(event: FileUploadHandlerEvent): Promise<void> {
        this.pqgUploadMissingColumns = null;
        this.pqgUploadFileError = null;

        this.file = event.files[0];
        if (!this.file) {
            return;
        }

        let headers: string[];
        try {
            headers = await extractSpreadsheetHeaderRow(this.file);
        } catch {
            this.pqgUploadFileError = 'Could not read the file headers. Use a valid CSV or Excel (.xlsx / .xls) file.';
            return;
        }

        const missing = missingPqgRatesRequiredColumns(headers);
        if (missing.length) {
            this.pqgUploadMissingColumns = missing;
            return;
        }

        this.modalLoading = true;
        this.service.uploadPqgRates(this.file).subscribe({
            next: () => {
                this.appService.toast('PQG rates uploaded successfully.', 'success');
                this.onPqgUploadModalHide();
                this.reloadTable$.next();
            }
        });
    }

    private applyContextFilters(params: Record<string, any>, asFilterObject = false): Record<string, any> {
        const displayName = this.displayAgencyName?.trim();
        if (displayName) {
            return asFilterObject
                ? applyDisplayAgencyNameToFilterObject(params, displayName)
                : applyDisplayAgencyNameToQuery(params, displayName);
        }

        return asFilterObject
            ? applyDistrictAndCompetitorToFilterObject(params, {
                  districtMappingId: this.districtMappingId,
                  competitorId: this.competitorId,
              })
            : applyDistrictAndCompetitorToQuery(params, {
                  districtMappingId: this.districtMappingId,
                  competitorId: this.competitorId,
              });
    }

    setColumns(): void {
        this.columns = buildRateColumns(this.preset, {
            hasDistrictMapping: !!this.districtMappingId,
            hasCompetitor: !!this.competitorId || !!this.displayAgencyName?.trim(),
            autocomplete: this.ratesAutocomplete.bind(this),
            navigate: (agency, district_name_mapped) =>
                this.router.navigate(['/rates'], {
                    queryParams: {
                        agency_name_mapped: agency,
                        district_name: district_name_mapped
                    }
                }),
            rateUnits$: (context = {}) => this.service.options(this.applyContextFilters(context)).pipe(
                map((o) => o.rate_units.map((v) => ({ label: v, value: v })))
            ),
            years$: (context = {}) => this.service.options(this.applyContextFilters(context)).pipe(
                map((o) => o.years.map((v) => ({ label: v, value: v })))
            ),
            docaVersions$: (context = {}) => this.service.options(this.applyContextFilters(context)).pipe(
                map((o) => (o.doca_versions ?? []).map((v) => ({ label: String(v), value: v })))
            )
        });

        this.applyQueryParamsToColumns(this.route.snapshot.queryParams);
    }

    private startVisibilitySyncPolling(): void {
        timer(0, 5000).pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap(() => this.service.visibilitySyncStatus()),
            tap((status) => this.handleVisibilitySyncStatus(status)),
        ).subscribe();
    }

    private handleVisibilitySyncStatus(status: RatesVisibilitySyncStatus | null): void {
        if (!status) {
            this.visibilitySyncStatus = null;
            return;
        }

        if (status.is_active || status.status === 'failed') {
            this.visibilitySyncStatus = status;
            return;
        }

        this.visibilitySyncStatus = null;

        if (status.status === 'completed' && status.id !== this.lastReloadedSyncRunId) {
            this.lastReloadedSyncRunId = status.id;
            this.reloadTable$.next();
        }
    }

    private applyQueryParamsToColumns(params: Params): void {
        const mappings: {
            param: string;
            field: string;
            asArray?: boolean;
        }[] = [
            { param: 'district_name', field: 'district_name_mapped', asArray: true },
            { param: 'service_name_mapped', field: 'service_name_mapped', asArray: true },
            { param: 'agency_name_mapped', field: 'agency_name_mapped', asArray: true },
            { param: 'rate_unit', field: 'rate_unit_mapped' },
            { param: 'rate_unit_mapped', field: 'rate_unit_mapped' }
        ];

        for (const { param, field, asArray } of mappings) {
            const column = this.columns.find((c) => c.field === field);
            if (!column) {
                continue;
            }

            const value = params[param];
            if (!value) {
                column.filterDefaultValue = null;
                continue;
            }

            const normalizedValue = asArray ? [value] : value;
            if (column.filterDefaultValue !== normalizedValue) {
                column.filterDefaultValue = normalizedValue;
            }
        }
    }
}
